import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import NewsModal from './NewsModal';
import { getPublicationHref, NEWS_HUB_PATH } from '../lib/newsPublications';
import { fetchFeaturedPublications } from '../lib/newsPublicationsApi';

const DEFAULT_IMAGE = '/logo.svg';

const NewsSection = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })],
  );
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    fetchFeaturedPublications().then(setNewsItems);
  }, []);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  const openItem = (news) => {
    const href = getPublicationHref(news);
    if (href) return;
    setSelectedNews(news);
  };

  return (
    <>
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2 className="text-3xl md:text-4xl font-bold">Новости</h2>
            <Link to={NEWS_HUB_PATH} className="text-sm font-medium text-accent hover:underline">
              Все новости →
            </Link>
          </div>

          <div className="relative">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex gap-6">
                {newsItems.map((news) => {
                  const href = getPublicationHref(news);
                  const card = (
                    <Card className="bg-white rounded-lg overflow-hidden shadow-lg border border-border/50 hover:shadow-xl transition-all duration-300 h-full cursor-pointer">
                      <div className="w-full h-48 sm:h-56 bg-gray-100 overflow-hidden flex items-center justify-center">
                        <img
                          src={news.image || news.previewImage}
                          alt={news.title}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            e.target.src = DEFAULT_IMAGE;
                          }}
                        />
                      </div>
                      <CardContent className="p-6">
                        <div className="text-sm text-accent mb-3 font-medium">{news.date}</div>
                        <h3 className="text-lg font-semibold mb-3 line-clamp-2">{news.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-3">{news.description}</p>
                        <Button variant="link" className="mt-3 p-0 h-auto text-blue-600 hover:text-blue-800">
                          Читать далее →
                        </Button>
                      </CardContent>
                    </Card>
                  );

                  return (
                    <div
                      className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.33%] min-w-0"
                      key={news.id}
                    >
                      {href ? (
                        href.startsWith('http') ? (
                          <a href={href} className="block h-full" target="_blank" rel="noopener noreferrer">
                            {card}
                          </a>
                        ) : (
                          <Link to={href} className="block h-full">{card}</Link>
                        )
                      ) : (
                        <button type="button" className="block h-full w-full text-left" onClick={() => openItem(news)}>
                          {card}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Button
              variant="outline"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 z-10 rounded-full h-8 w-8 sm:h-10 sm:w-10 bg-white/90 hover:bg-white shadow-lg hidden sm:flex"
              onClick={scrollPrev}
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 z-10 rounded-full h-8 w-8 sm:h-10 sm:w-10 bg-white/90 hover:bg-white shadow-lg hidden sm:flex"
              onClick={scrollNext}
            >
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          </div>
        </div>
      </section>

      <NewsModal
        news={selectedNews}
        isOpen={!!selectedNews}
        onClose={() => setSelectedNews(null)}
      />
    </>
  );
};

export default NewsSection;
